import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { ulGtpDataVolumeCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the UlGtpDataVolumeCell component
 */
interface UlGtpDataVolumeCellProps {
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
}

/**
 * Uplink GTP Data Volume Chart (LTE Cell Level)
 * 
 * Displays uplink GPRS Tunneling Protocol (GTP) data volume metrics for individual LTE cells.
 * Shows the total volume of uplink data transmitted through the cell over time.
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level UL data volume metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <UlGtpDataVolumeCell
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (ulGtpDataVolumeCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/ul-gtp-data-volume?cell_name={cellName}
 */

const UlGtpDataVolumeCell: React.FC<UlGtpDataVolumeCellProps> = ({ 
  onFullscreenToggle, 
  isFullscreen,
  cellName
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={ulGtpDataVolumeCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default UlGtpDataVolumeCell;
